import React, { useState, useEffect } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Grid } from '@mui/material';
import {
    Select,
    DateInput,
    SecondaryButton,
    CommonModal,
    LoadingButtonV4,
} from '../../Common';
import Enums from '../../../enums';
import Routes from '../../../routes';
import { postRequestHeaders } from '../../../utils';
import {
    formatFinalData,
    menuProps,
    enabledServiceEventTypes,
    determineServiceOptions,
    filterTechniciansForService,
} from '../../ServiceEvents/utils';
import { TranslateW } from '../../../Utils/Languages/Translate';
import { TranslateArray } from '../../../Utils/Languages/TranslateDropdown';
import { useIntl } from 'react-intl';
import useNavigate from '../../../hooks/useNavigate';

const AddServiceEvent = ({
    payload: {
        id: workOrderId
    },
    modalOpen,
    onClose,
    history = {},
}) => {
    const intl = useIntl();
    const [assetId, setAssetId] = useState('');
    const [workItemTypeId, setWorkItemTypeId] = useState('');
    const [primaryTechnicianId, setPrimaryTechnicianId] = useState('');
    const [serviceDate, setServiceDate] = useState(null);
    const [workOrderState, setWorkOrderState] = useState(null);
    const [options, setOptions] = useState({
        assets: [],
        workItemTypes: [],
        savedWorkItemTypes: [],
        technicians: [],
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate({
        pathname: 'AddServiceEvent'
    });

    const resetState = () => {
        setAssetId('');
        setWorkItemTypeId('');
        setPrimaryTechnicianId('');
        setServiceDate('');
        setErrors({});
        setLoading(false);
    };

    const closeModal = () => {
        resetState();
        onClose();
    };

    const validateFields = () => {
        const errorObject = {};
        setErrors(errorObject);
        let fieldsAreValid = true;

        const selectedAsset = options.assets.find(a => a.id ===  assetId) || {};
        const { installDate, disbursementDate } = selectedAsset;
        const formattedInstallDate = moment(installDate).utc().format('MM/DD/YYYY');
        const formattedDisbursementDate = moment(disbursementDate).utc().format('MM/DD/YYYY');
        const serviceDateBeforeInstallDate = moment(serviceDate).isBefore(selectedAsset.installDate, 'day');
        const serviceDateAfterShutdownDate = moment(serviceDate).isAfter(disbursementDate, 'day');

        if (!assetId) {
            errorObject.assetId = true;
            fieldsAreValid = false;
        }
        if (!workItemTypeId) {
            errorObject.workItemTypeId = true;
            fieldsAreValid = false;
        }
        if (!primaryTechnicianId) {
            errorObject.primaryTechnicianId = true;
            fieldsAreValid = false;
        }
        if (!serviceDate) {
            errorObject.serviceDate = 'This field is required';
            fieldsAreValid = false;
        }
        if (serviceDateBeforeInstallDate || serviceDateAfterShutdownDate) {
            errorObject.serviceDate = serviceDateBeforeInstallDate
                ? `Service Date Cannot Be Before Install Date (${formattedInstallDate})`
                : `Service Date Cannot Be After Current Date (${formattedDisbursementDate})`;
            fieldsAreValid = false;
        }
        setErrors(errorObject);
        return fieldsAreValid;
    };

    const submit = async () => {
        const fieldsAreValid = validateFields();
        const isSaveForLater = true;

        if (!fieldsAreValid) {
            return null;
        }

        setLoading(true);

        // installs, recoveries & userWeightUnits must all be null
        const final_data = formatFinalData(
            workItemTypeId,
            {
                ServiceEventCreationType: "saveForLater",
                workOrderId,
                assetId,
                workItemTypeId,
                primaryTechnicianId,
                serviceDate,
                installs: [],
                recoveries: [],
                userWeightUnits: ''
            },
            isSaveForLater
        );
        console.log('Quick Create Service Event final_data', final_data);
        const response = await fetch(Routes.ServiceEvent.AddServiceEvent(), {
            method : "POST",
            headers: {...postRequestHeaders},
            body : JSON.stringify(final_data)
        });
        const data = await response.json();
        console.log('response', data);

        if(response.status === 200) {
            toast.success('The service event has been successfully created.', { duration: 6000 });
            resetState();
            window.scrollTo(0, 0);
            return onClose();
        }
        return toast.error('There was an error creating the Service Event. Please try again.', { duration: 6000 });
    };

    const addMoreDetails = () => {
        const fieldsAreValid = validateFields();
        if (!fieldsAreValid) return null;
        const data = {
            workOrderId,
            assetId,
            workItemTypeId,
            primaryTechnicianId,
            serviceDate,
        };
        return navigate.push(`/CreateServiceEvent/${workOrderId}`, {state: { serviceEventData: data }});
    };

    const handleAssetSelect = (selectedAssetId) => {
        setPrimaryTechnicianId('')
        setWorkItemTypeId('');
        setServiceDate(null);
        setAssetId(selectedAssetId);
        const selectedAsset = options.assets.find(a => a.id ===  selectedAssetId);
        // serviceEventId does not exist yet
        const serviceEventId = '';
        const { coolingApplianceStatusId } = selectedAsset;
        const assetUnderRepair = coolingApplianceStatusId === Enums.SE.CoolingApplianceStatus.UnderRepair;
        const workItemTypes = determineServiceOptions(
            assetUnderRepair,
            selectedAsset,
            options.savedWorkItemTypes,
            serviceEventId
        );
        const asset = {
            ...selectedAsset,
            maxGasCapacity: selectedAsset.gasChargeCapacityLbs,
        };
        const finalTechniciansList = filterTechniciansForService(
            asset,
            options.technicians,
            workOrderState === 'CA'
        );
        const translatedWorkItemTypes = TranslateArray(intl, workItemTypes, "WorkItemType");
        return setOptions(prevOptions => ({
            ...prevOptions,
            workItemTypes: translatedWorkItemTypes,
            technicians: finalTechniciansList
        }));
    };

    useEffect(() => {
        const fetchDropDownData = async () => {
            // must pass empty servivce event ID to fetch drop down options
            const seID = '';
            const response = await fetch(Routes.ServiceEvent.AddServiceEventDropdowns(workOrderId, seID));
            let {
                assets = [],
                workItemTypes = [],
                technicians = [],
                workOrderPhysicalState = '',
            } = await response.json();
            workItemTypes = TranslateArray(intl, workItemTypes, "WorkItemType");
            // need to save an unfiltered clone to filter each time selected asset changes
            const savedWorkItemTypes = workItemTypes;

            if (localStorage.getItem("isprod") === 'true') {
                workItemTypes = workItemTypes.filter(type => enabledServiceEventTypes.includes(type.id));
            }

            setWorkOrderState(workOrderPhysicalState);
            setOptions({ assets, workItemTypes, savedWorkItemTypes, technicians });
            return setLoading(false);
        };

        if (workOrderId) {
            fetchDropDownData();
        }
    }, [workOrderId, intl]);

    return (
        <CommonModal
            open={modalOpen}
            close={closeModal}
            header="Create Service Event"
        >
            <Grid container spacing={2} justifyContent='center'>
                <Grid item xs={8} align='center'>
                    <Select
                        required
                        variant='standard'
                        label={TranslateW("EquipmentWorkedOn")}
                        value={assetId}
                        onChange={e => handleAssetSelect(e.target.value)}
                        options={options.assets}
                        disabled={loading}
                        menuProps={menuProps}
                        error={errors.assetId}
                    />
                </Grid>
                <Grid item xs={8} align='center'>
                    <Select
                        required
                        variant='standard'
                        label={TranslateW("TypeOfService")}
                        value={workItemTypeId}
                        onChange={e => setWorkItemTypeId(e.target.value)}
                        options={options.workItemTypes}
                        disabled={loading || !assetId}
                        menuProps={menuProps}
                        error={errors.workItemTypeId}
                    />
                </Grid>
                <Grid item xs={8} align='center'>
                    <Select
                        required
                        variant='standard'
                        label={TranslateW("serviceEventView.primary-technician")}
                        value={primaryTechnicianId}
                        onChange={e => setPrimaryTechnicianId(e.target.value)}
                        options={options.technicians}
                        disabled={loading || !assetId || !workItemTypeId}
                        menuProps={menuProps}
                        error={errors.primaryTechnicianId}
                    />
                </Grid>
                <Grid item xs={8} align='center'>
                    <DateInput
                        required
                        allowPast
                        allowFuture={false}
                        variant='standard'
                        date={serviceDate}
                        onChange={new_date => setServiceDate(new_date)}
                        label={`${TranslateW("serviceEventView.service-date")} *`}
                        disabled={loading}
                        error={errors.serviceDate}
                        customHelperText={errors.serviceDate}
                    />
                </Grid>
                <Grid item xs={8} mt={2} align='center'>
                    <LoadingButtonV4
                        useLargeButton
                        buttonText='Create Service Event'
                        onClick={submit}
                        loading={loading}
                    />
                </Grid>
                <Grid item xs={8} mb={2} align='center'>
                    <SecondaryButton
                        useLargeButton
                        fullWidth
                        buttonText='Add More Details'
                        onClick={addMoreDetails}
                    />
                </Grid>
            </Grid>
        </CommonModal>
    );
};

export default AddServiceEvent;
